/* eslint-disable camelcase */
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useCallback, useState } from 'react';

// CorrectiveActionDocument type
export type CorrectiveActionDocument = {
    ca_document_id?: string;
    corrective_action_id: number;
    ca_document_name?: string;
    upload_date?: string;
    upload_by?: string;
    signed_url?: string;
};

type MultiFileViewerProps = {
    files: CorrectiveActionDocument[];
    loading?: boolean;
    allowDelete?: boolean;
    onDelete?: (file: CorrectiveActionDocument) => void;
};

const MultiFileViewer: React.FC<MultiFileViewerProps> = ({
    files,
    loading = false,
    allowDelete = false,
    onDelete,
}) => {
    const [imagesLoading, setImagesLoading] = useState<boolean>(false);

    // useEffect(() => {
    //     // When new files are passed → reset images loading state
    //     setImagesLoading(true);
    // }, [files]);

    const handleImageLoad = useCallback(() => {
        const imageFiles = files.filter((file) => /\.(png|jpe?g)$/i.test(file.ca_document_name || ''));
        const loadedImages = document.querySelectorAll('.multi-file-viewer-img.loaded').length;

        if (loadedImages >= imageFiles.length) {
            setImagesLoading(false);
        } else if (loadedImages < imageFiles.length) {
            setImagesLoading(true);
        }
    }, [files]);

    const getFileLabel = (ca_document_name?: string) => {
        const ext = ca_document_name?.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return '📄 PDF';
        if (ext === 'doc' || ext === 'docx') return '📝 Word Doc';
        if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') return '🖼️ Image';
        return '📁 File';
    };

    const confirmDelete = (file: CorrectiveActionDocument) => {
        confirmDialog({
            message: `Are you sure you want to delete "${file.ca_document_name}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            accept: () => {
                if (onDelete) {
                    onDelete(file);
                }
            },
        });
    };

    return (
        <div style={{ fontFamily: 'Arial', padding: '1rem' }}>
            {/* ConfirmDialog component must be present in DOM */}
            <ConfirmDialog />

            {(loading || (imagesLoading && files.length > 0)) && (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem' }}>
                    <ProgressSpinner />
                </div>
            )}
            <h6>Attachments</h6>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {files.length > 0 &&
                    files.map((file, index) => {
                        const isImage = /\.(png|jpe?g)$/i.test(file.ca_document_name || '');

                        return (
                            <div
                                key={file.ca_document_id || index}
                                style={{
                                    width: '140px',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    textAlign: 'center',
                                    position: 'relative',
                                    background: '#fefefe',
                                }}
                                title={file.ca_document_name}
                            >
                                {isImage ? (
                                    <img
                                        src={file.signed_url}
                                        alt={file.ca_document_name}
                                        className="multi-file-viewer-img"
                                        onLoad={(e) => {
                                            e.currentTarget.classList.add('loaded');
                                            handleImageLoad();
                                        }}
                                        onError={(e) => {
                                            e.currentTarget.classList.add('loaded');
                                            handleImageLoad();
                                        }}
                                        style={{
                                            width: '100%',
                                            height: '100px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            height: '100px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            fontSize: '14px',
                                            wordBreak: 'break-word',
                                            color: '#555',
                                        }}
                                    >
                                        <div>{getFileLabel(file.ca_document_name)}</div>
                                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                            {file.ca_document_name}
                                        </div>
                                    </div>
                                )}

                                <div
                                    style={{
                                        marginTop: '10px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <Button
                                        icon="pi pi-external-link"
                                        className="p-button-text p-button-sm"
                                        onClick={() => window.open(file.signed_url, '_blank')}
                                        tooltip="Open"
                                    />

                                    {allowDelete && onDelete && (
                                        <Button
                                            icon="pi pi-trash"
                                            className="p-button-text p-button-sm p-button-danger"
                                            onClick={() => confirmDelete(file)}
                                            tooltip="Delete"
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default MultiFileViewer;
